import { gql, useMutation, useApolloClient } from '@apollo/client';
import { showToast } from '../../hooks/useToast';
import { playSound } from "../../hooks/useSound";

/* ============================================
   GRAPHQL MUTATION DEFINITIONS
   ============================================ */

// Create rider.
const CREATE_RIDER = gql`
  mutation CreateRider(
    $email: EmailAddress!
    $password: String!
    $name: String!
    $phoneNumber: String!
  ) {
    createRider(
      email: $email
      password: $password
      name: $name
      phoneNumber: $phoneNumber
    ) {
      id
      email
      name
      phoneNumber
      currentRideId
      completedRides
      homeAddress
    }
  }
`;

// Update rider.
const UPDATE_RIDER = gql`
  mutation UpdateRider(
    $id: ID!
    $email: EmailAddress
    $name: String
    $phoneNumber: String
    $currentRideId: ID
    $completedRides: Int
    $homeAddress: String
    $workAddress: String
    $profilePicture: String
  ) {
    updateRider(
      id: $id
      email: $email
      name: $name
      phoneNumber: $phoneNumber
      currentRideId: $currentRideId
      completedRides: $completedRides
      homeAddress: $homeAddress
      workAddress: $workAddress
      profilePicture: $profilePicture
    ) {
      id
      email
      name
      phoneNumber
      currentRideId
      completedRides
      homeAddress
      workAddress
      profilePicture
    }
  }
`;

// Delete rider.
const DELETE_RIDER = gql`
  mutation DeleteRider($id: ID!) {
    deleteRider(id: $id) {
      message
    }
  }
`;

// Add a favorite driver to a rider.
const ADD_FAVORITE_DRIVER = gql`
  mutation AddFavoriteDriver($riderId: ID!, $driverId: ID!) {
    addFavoriteDriver(riderId: $riderId, driverId: $driverId) {
      id
      email
      name
      phoneNumber
      currentRideId
      completedRides
      homeAddress
    }
  }
`;

// Remove a favorite driver to a rider.
const REMOVE_FAVORITE_DRIVER = gql`
  mutation RemoveFavoriteDriver($riderId: ID!, $driverId: ID!) {
    removeFavoriteDriver(riderId: $riderId, driverId: $driverId) {
      id
      email
      name
      phoneNumber
      currentRideId
      completedRides
      homeAddress
    }
  }
`;

// Reset rider's ride flow status to NONE.
const RESET_RIDER_RIDE_FLOW_STATUS = gql`
  mutation ResetRiderRideFlowStatus($id: ID!) {
    resetRiderRideFlowStatus(id: $id) {
      id
      rideFlowStatus
    }
  }
`;

/* ============================================
   MUTATION HOOKS
   ============================================ */

// Create a new rider.
export const useCreateRider = () => {
  const [
    createRider,
    { loading: loadingCreateRider, error: errorCreateRider },
  ] = useMutation(CREATE_RIDER, {
    onError: () => {
      // TODO: Delete console logs.
      // console.log('Create Rider Error.');
    },
    onCompleted: (data) => {
      // TODO: Delete console logs.
      // console.log('Rider Created Successfully.');
      // console.log(`Return data: `, data.createRider);
    },
  });

  return { createRider, loadingCreateRider, errorCreateRider };
};

// Update an existing rider.
export const useUpdateRider = () => {
  const [
    updateRider,
    { loading: loadingUpdateRider, error: errorUpdateRider },
  ] = useMutation(UPDATE_RIDER, {
    onError: () => {
      // TODO: Delete console logs.
      // console.log('Update Rider Error.');
    },
    onCompleted: (data) => {
      // TODO: Delete console logs.
      // console.log('Rider Updated Successfully.');
      // console.log(`Return data: `, data.updateRider);
    },
  });

  return { updateRider, loadingUpdateRider, errorUpdateRider };
};

// Delete an existing rider.
export const useDeleteRider = () => {
  const [
    deleteRider,
    { loading: loadingDeleteRider, error: errorDeleteRider },
  ] = useMutation(DELETE_RIDER, {
    onError: () => {
      // TODO: Delete console logs.
      // console.log('Delete Rider Error.');
    },
    onCompleted: (data) => {
      // TODO: Delete console logs.
      // console.log('Rider Deleted Successfully.');
      // console.log(`Return data: `, data.deleteRider);
    },
  });

  return { deleteRider, loadingDeleteRider, errorDeleteRider };
};

// Add a favorite driver.
export const useAddFavoriteDriver = () => {
  const client = useApolloClient();

  const [
    addFavoriteDriver,
    { loading: loadingAddFavoriteDriver, error: errorAddFavoriteDriver },
  ] = useMutation(ADD_FAVORITE_DRIVER, {
    onError: (error) => {
      console.error('Add Favorite Driver Error:', error);
      showToast.error(`Failed to add driver: ${error.message}`);
      playSound("alert");
    },
    onCompleted: (data) => {
      // TODO: Delete console logs.
      // console.log('Driver Added to Favorites Successfully.');
      // console.log(`Return data: `, data.addFavoriteDriver);
      showToast.success('Driver added to favorites!');
      playSound("success");

      // Invalidate relevant queries in the cache to ensure fresh data.
      client.cache.evict({ fieldName: 'getFavoriteDrivers' });
      client.cache.gc();
    },
  });

  return {
    addFavoriteDriver,
    loadingAddFavoriteDriver,
    errorAddFavoriteDriver,
  };
};

// Remove a favorite driver.
export const useRemoveFavoriteDriver = () => {
  //TODO: Delete in production.
  // console.log('Initializing useRemoveFavoriteDriver hook');

  const client = useApolloClient();

  const [
    removeFavoriteDriver,
    { loading: loadingRemoveFavoriteDriver, error: errorRemoveFavoriteDriver },
  ] = useMutation(REMOVE_FAVORITE_DRIVER, {
    onError: (error) => {
      console.error('Remove Favorite Driver Error:', error);
      showToast.error(`Failed to remove driver: ${error.message}`);
      playSound("alert");
    },
    onCompleted: (data) => {
      // TODO: Delete console logs.
      // console.log('Driver Removed from Favorites Successfully.');
      // console.log(`Return data from hook: `, data);
      showToast.success('Driver removed from favorites');
      playSound("success");

      // Invalidate relevant queries in the cache to ensure fresh data.
      client.cache.evict({ fieldName: 'getFavoriteDrivers' });
      client.cache.gc();
    },
  });

  return {
    removeFavoriteDriver,
    loadingRemoveFavoriteDriver,
    errorRemoveFavoriteDriver,
  };
};

// Reset rider's ride flow status to NONE (when leaving completed ride page).
export const useResetRiderRideFlowStatus = () => {
  const [
    resetRiderRideFlowStatus,
    { loading: loadingResetRideFlow, error: errorResetRideFlow },
  ] = useMutation(RESET_RIDER_RIDE_FLOW_STATUS, {
    onError: (error) => {
      console.error('Reset Ride Flow Status Error:', error);
    },
    onCompleted: () => {
      // Silent completion - no toast needed
    },
  });

  return {
    resetRiderRideFlowStatus,
    loadingResetRideFlow,
    errorResetRideFlow,
  };
};
